/**
 * Guided Tours (add-on) UI state.
 *
 * - tours/progress: eligible tours for the current user + per-tour completion
 *   (fetched from addons/tours/list).
 * - recording: the in-app recorder (navigation/action modes, draft steps).
 * - playing: the active tour playback (driven by TourPlayer).
 */
import { createSlice } from '@reduxjs/toolkit';

const initialState = {
  tours: [],
  progress: {}, // { [tourId]: { status, stepIndex, completedAt } }
  loaded: false,
  recording: {
    isRecording: false,
    mode: 'navigation', // 'navigation' | 'action'
    tourId: null, // set when re-recording an existing tour
    draftTour: null, // { name, description, trigger, status, startUrl }
    steps: [],
  },
  playing: {
    tourId: null,
    stepIndex: 0,
    preview: false, // admin preview: no progress is persisted
    tourData: null, // inline tour (preview of drafts not in the eligible list)
  },
};

const toursSlice = createSlice({
  name: 'tours',
  initialState,
  reducers: {
    setTours: (state, action) => {
      state.tours = action.payload?.tours || [];
      state.progress = action.payload?.progress || {};
      state.loaded = true;
    },
    invalidateTours: (state) => {
      state.loaded = false;
    },
    setTourProgressEntry: (state, action) => {
      const { tourId, ...entry } = action.payload || {};
      if (!tourId) return;
      state.progress[tourId] = { ...(state.progress[tourId] || {}), ...entry };
    },
    startRecording: (state, action) => {
      const { tourId = null, draftTour = null, steps = [] } = action.payload || {};
      state.recording = { isRecording: true, mode: 'navigation', tourId, draftTour, steps };
    },
    setRecordingMode: (state, action) => {
      state.recording.mode = action.payload === 'action' ? 'action' : 'navigation';
    },
    addRecordedStep: (state, action) => {
      if (action.payload) state.recording.steps.push(action.payload);
    },
    updateRecordedStep: (state, action) => {
      const { index, step } = action.payload || {};
      if (state.recording.steps[index]) state.recording.steps[index] = step;
    },
    removeRecordedStep: (state, action) => {
      state.recording.steps.splice(action.payload, 1);
    },
    moveRecordedStep: (state, action) => {
      const { from, to } = action.payload || {};
      const steps = state.recording.steps;
      if (from == null || to == null || !steps[from] || to < 0 || to >= steps.length) return;
      const [moved] = steps.splice(from, 1);
      steps.splice(to, 0, moved);
    },
    stopRecording: (state) => {
      state.recording = { ...initialState.recording };
    },
    startPlaying: (state, action) => {
      const { tourId, stepIndex = 0, preview = false, tourData = null } = action.payload || {};
      state.playing = { tourId, stepIndex, preview, tourData };
    },
    setPlayingStep: (state, action) => {
      state.playing.stepIndex = action.payload || 0;
    },
    stopPlaying: (state) => {
      state.playing = { ...initialState.playing };
    },
    resetTours: () => initialState,
  },
});

export const {
  setTours,
  invalidateTours,
  setTourProgressEntry,
  startRecording,
  setRecordingMode,
  addRecordedStep,
  updateRecordedStep,
  removeRecordedStep,
  moveRecordedStep,
  stopRecording,
  startPlaying,
  setPlayingStep,
  stopPlaying,
  resetTours,
} = toursSlice.actions;

export default toursSlice.reducer;
